/**
 * Shrine Clone Pro - Global Theme Utilities & Components
 * Lightweight Vanilla JS / Web Components Architecture
 */

// Event Bus for cross-component reactive updates
window.themeBus = window.themeBus || {
  events: {},
  subscribe(event, callback) {
    if (!this.events[event]) this.events[event] = [];
    this.events[event].push(callback);
    return () => {
      this.events[event] = this.events[event].filter(cb => cb !== callback);
    };
  },
  publish(event, data) {
    if (this.events[event]) {
      this.events[event].forEach(cb => {
        try { cb(data); } catch (e) { console.error('themeBus listener error:', e); }
      });
    }
  }
};

// Utilities
function debounce(fn, wait) {
  let timeout;
  return function(...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn.apply(this, args), wait);
  };
}

function trapFocus(container, elementToFocus = container) {
  if (!container || !container.querySelectorAll) return;
  const elements = container.querySelectorAll(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  );
  const first = elements[0];
  const last = elements[elements.length - 1];

  removeTrapFocus(container);

  container._focusHandler = function(e) {
    if (e.key !== 'Tab') return;
    if (e.shiftKey) {
      if (document.activeElement === first) {
        last.focus();
        e.preventDefault();
      }
    } else {
      if (document.activeElement === last) {
        first.focus();
        e.preventDefault();
      }
    }
  };

  container.addEventListener('keydown', container._focusHandler);
  if (elementToFocus) elementToFocus.focus();
}

function removeTrapFocus(container) {
  if (container && container._focusHandler) {
    container.removeEventListener('keydown', container._focusHandler);
    delete container._focusHandler;
  }
}

// Scroll Reveal Observer
class ScrollReveal {
  constructor() {
    this.elements = document.querySelectorAll('[data-reveal]:not(.is-revealed)');
    if (!this.elements.length) return;

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealed');
            this.observer.unobserve(entry.target);
          }
        });
      }, {
        rootMargin: '0px 0px -40px 0px',
        threshold: 0.1
      });

      this.elements.forEach(el => this.observer.observe(el));
    } else {
      this.elements.forEach(el => el.classList.add('is-revealed'));
    }
  }
}

// Product Gallery Controller: touch swipeable slider on mobile, drag-to-slide, arrows, counter, & thumbnail synchronization
function initProductGallery() {
  const galleries = document.querySelectorAll('.product-gallery');
  galleries.forEach(gallery => {
    if (gallery.dataset.galleryInitialized === 'true') return;
    gallery.dataset.galleryInitialized = 'true';

    const main = gallery.querySelector('.product-gallery__main');
    if (!main) return;

    const slides = Array.from(gallery.querySelectorAll('.product-gallery__slide'));
    const thumbs = Array.from(gallery.querySelectorAll('.product-gallery__thumb-btn'));
    const dots = Array.from(gallery.querySelectorAll('.product-gallery__dot'));
    const counterCurrent = gallery.querySelector('.product-gallery__counter-current');
    const prevArrow = gallery.querySelector('.product-gallery__arrow--prev');
    const nextArrow = gallery.querySelector('.product-gallery__arrow--next');

    let isProgrammaticScroll = false;
    let scrollDebounceTimer = null;

    function getActiveIndex() {
      const isMobile = window.innerWidth < 990;
      if (isMobile && main.scrollWidth > main.clientWidth) {
        const slideWidth = main.clientWidth || 1;
        return Math.min(Math.max(Math.round(main.scrollLeft / slideWidth), 0), slides.length - 1);
      }
      const activeIdx = slides.findIndex(s => s.classList.contains('is-active'));
      return activeIdx >= 0 ? activeIdx : 0;
    }

    function syncState(index, scrollToSlide = false) {
      if (index < 0 || index >= slides.length) return;

      // Update slides active class
      slides.forEach((slide, idx) => {
        if (idx === index) {
          slide.classList.add('is-active');
        } else {
          slide.classList.remove('is-active');
          const vid = slide.querySelector('video');
          if (vid && !vid.paused) vid.pause();
        }
      });

      // Update thumbnails active class & smooth scroll into view
      thumbs.forEach((thumb, idx) => {
        if (idx === index) {
          thumb.classList.add('is-active');
          thumb.setAttribute('aria-selected', 'true');
          thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        } else {
          thumb.classList.remove('is-active');
          thumb.setAttribute('aria-selected', 'false');
        }
      });

      // Update dots active class
      dots.forEach((dot, idx) => {
        if (idx === index) {
          dot.classList.add('is-active');
          dot.setAttribute('aria-selected', 'true');
        } else {
          dot.classList.remove('is-active');
          dot.setAttribute('aria-selected', 'false');
        }
      });

      // Update slide counter badge
      if (counterCurrent) {
        counterCurrent.textContent = index + 1;
      }

      // Scroll mobile slider if requested
      if (scrollToSlide) {
        const isMobile = window.innerWidth < 990;
        if (isMobile && main.scrollWidth > main.clientWidth) {
          isProgrammaticScroll = true;
          const targetSlide = slides[index];
          const targetLeft = targetSlide ? targetSlide.offsetLeft : (index * main.clientWidth);
          main.scrollTo({
            left: targetLeft,
            behavior: 'smooth'
          });
          setTimeout(() => {
            isProgrammaticScroll = false;
          }, 350);
        }
      }
    }

    // Mobile swipe / scroll listener with debounced thumbnail & dot sync
    main.addEventListener('scroll', () => {
      if (isProgrammaticScroll) return;
      clearTimeout(scrollDebounceTimer);
      scrollDebounceTimer = setTimeout(() => {
        const activeIdx = getActiveIndex();
        syncState(activeIdx, false);
      }, 40);
    }, { passive: true });


    // Desktop Mouse Drag-to-Slide (never interferes with mobile touch swiping)
    if (!('ontouchstart' in window)) {
      let isMouseDown = false;
      let startX = 0;
      let initialScroll = 0;
      let draggedDistance = 0;

      main.addEventListener('mousedown', (e) => {
        if (e.button !== 0) return;
        if (e.target.closest('.product-gallery__zoom-trigger, .product-gallery__arrow')) return;
        isMouseDown = true;
        draggedDistance = 0;
        startX = e.pageX;
        initialScroll = main.scrollLeft;
        main.style.cursor = 'grabbing';
      });

      window.addEventListener('mousemove', (e) => {
        if (!isMouseDown) return;
        const deltaX = e.pageX - startX;
        draggedDistance = Math.abs(deltaX);
        if (draggedDistance > 5) {
          main.scrollLeft = initialScroll - deltaX;
        }
      });

      window.addEventListener('mouseup', () => {
        if (!isMouseDown) return;
        isMouseDown = false;
        main.style.cursor = '';
        if (draggedDistance > 25) {
          const slideWidth = main.clientWidth || 1;
          const targetIdx = Math.min(Math.max(Math.round(main.scrollLeft / slideWidth), 0), slides.length - 1);
          syncState(targetIdx, true);
        }
      });
    }

    // Arrow Navigation
    if (prevArrow) {
      prevArrow.addEventListener('click', (e) => {
        e.preventDefault();
        const currentIdx = getActiveIndex();
        const newIdx = (currentIdx - 1 + slides.length) % slides.length;
        syncState(newIdx, true);
      });
    }

    if (nextArrow) {
      nextArrow.addEventListener('click', (e) => {
        e.preventDefault();
        const currentIdx = getActiveIndex();
        const newIdx = (currentIdx + 1) % slides.length;
        syncState(newIdx, true);
      });
    }

    // Thumbnail Click Handling
    thumbs.forEach((thumb, idx) => {
      thumb.addEventListener('click', (e) => {
        e.preventDefault();
        syncState(idx, true);
      });
    });

    // Dots Click Handling
    dots.forEach((dot, idx) => {
      dot.addEventListener('click', (e) => {
        e.preventDefault();
        syncState(idx, true);
      });
    });
  });

  // Variant change auto-selection
  if (window.themeBus && !window._variantGallerySubscribed) {
    window._variantGallerySubscribed = true;
    window.themeBus.subscribe('variant:changed', (data) => {
      if (!data || !data.variant || !data.variant.featured_media) return;
      const mediaId = String(data.variant.featured_media.id);
      const targetThumb = document.querySelector(`.product-gallery__thumb-btn[data-media-id="${mediaId}"]`);
      if (targetThumb) {
        targetThumb.click();
      }
    });
  }
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  new ScrollReveal();
  initProductGallery();
});

// Re-init on section load in Theme Editor
document.addEventListener('shopify:section:load', () => {
  new ScrollReveal();
  initProductGallery();
});

